export function recordAndCheckRateLimit(
  submissionsByIp: Map<string, number[]>,
  ip: string,
  now: number,
  windowMs: number,
  maxRequests: number,
): boolean {
  const recent = (submissionsByIp.get(ip) ?? []).filter((timestamp) => now - timestamp < windowMs);
  recent.push(now);
  submissionsByIp.set(ip, recent);
  return recent.length > maxRequests;
}

export function sanitizeContactField(value: FormDataEntryValue | null, maxLength: number): string {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

/**
 * Posts the marketing contact form and reports whether the submission
 * succeeded.
 *
 * The success outcome is decided purely by the API response — a best-effort
 * analytics call fires only *after* that decision, in its own try/catch, so a
 * `captureImpl` throw (e.g. an internal/serialization/transport error inside
 * posthog-js) can never turn a delivered submission into a reported failure.
 * `fetchImpl`/`captureImpl` are injected so this ordering is unit-testable
 * without a browser environment.
 */
export async function submitContactForm(
  data: FormData,
  fetchImpl: typeof fetch,
  captureImpl: (event: string) => void,
): Promise<'success' | 'error'> {
  try {
    const res = await fetchImpl('/api/contact', {
      method: 'POST',
      body: data,
    });

    if (!res.ok) return 'error';

    try {
      captureImpl('contact_submitted');
    } catch {
      // Analytics failures must never mask a delivered submission.
    }
    return 'success';
  } catch {
    return 'error';
  }
}
